import type { Dispatch, FC, SetStateAction } from 'react';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { DialogProps } from '@mui/material';
import {
  Dialog,
  DialogContent,
  Divider,
  Grid,
  Stack,
  ToggleButton,
  Typography,
} from '@mui/material';
import type { FormikProps, FormikValues } from 'formik';
import { Form, Formik, useFormikContext } from 'formik';
import _ from 'lodash';
import type { InferType, SchemaOf } from 'yup';
import * as Yup from 'yup';

import {
  PERMISSION_GROUP_NAME,
  PermissionGroup,
} from 'src/constants/modulePermission';
import { SECTIONS_WITH_READ_ONLY_OPTION } from 'src/constants/section';
import type { ModuleIdTypeMapProps } from 'src/types/modulePermission';
import type { BaseUser, User } from 'src/types/user';
import type { ModuleRole } from 'src/types/userModule';
import { usePrevious } from 'src/hooks/usePrevious';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpServiceInstance from 'src/services/api/http';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import useUserPermissions, {
  findCurrentSurveyPermissions,
} from 'src/components/app/context/UserContext/useUserPermissions';
import FilterDisplay from 'src/components/common/FilterDisplay';
import BulkEditDialogActions from 'src/components/userAdmin/UserBulkEditDialog/components/BulkEditDialogActions';
import BulkEditPermissionSummary from 'src/components/userAdmin/UserBulkEditDialog/components/BulkEditPermissionSummary/bulkEditPermissionSummary';
import BulkEditRadioGroup from 'src/components/userAdmin/UserBulkEditDialog/components/BulkEditRadioGroup';
import BulkEditStepper from 'src/components/userAdmin/UserBulkEditDialog/components/BulkEditStepper';
import BulkEditSummary from 'src/components/userAdmin/UserBulkEditDialog/components/BulkEditSummary';
import BulkEditUserRoleSummary from 'src/components/userAdmin/UserBulkEditDialog/components/BulkEditUserRoleSummary';
import { formatLabel } from 'src/components/userAdmin/UserListPage/filterTree';

import { styles } from './index.styles';

/**
 * The form values submitted when bulk editing users.
 *
 * Note: Actually, only a subset of these fields is submitted, but we need all fields in one
 * for formik.
 */
export interface BulkEditDialogFormValues {
  isUserActiveInSurvey: 'true' | 'false' | '';
  editType: 'modulePermission' | 'userAttributes' | '';
  role: string | '';
  hasAccess: 'true' | 'false' | '';
  includeAllSubnodes: 'true' | '';
  moduleId: string | '';
  permissionGroup: string | '';
  readOnly: 'true' | 'false' | '';
}

export const initialFormValues: BulkEditDialogFormValues = {
  editType: '',
  isUserActiveInSurvey: '',
  role: '',
  moduleId: '',
  permissionGroup: '',
  hasAccess: '',
  includeAllSubnodes: '',
  readOnly: '',
};

/**
 * The discrete steps during bulk editing.
 */
export enum BulkEditStep {
  SelectType = 1,
  ChangeAttributes = 2,
  ConfirmChanges = 3,
}

/**
 * Since all fields share the same structure, the form fields are described in a generic format
 * that allows us to support stuff like i18n.
 */
export interface FormFieldData {
  choices: {
    label: string;
    value: string;
  }[];
  label: string;
  name: string;
}

/**
 * Helper to construct a binary choice form field (which also has an "unchanged" choice).
 */
const binaryChoice = (name: string, label: string): FormFieldData => ({
  name,
  label,
  choices: [
    {
      label: 'common:unchanged',
      value: '',
    },
    {
      label: 'common:yes',
      value: 'true',
    },
    {
      label: 'common:no',
      value: 'false',
    },
  ],
});

const getRoleSelection = (user: User, selectedUsers: BaseUser[]) => {
  const userCanEditRoles = user.role?.canEditRoles;
  if (!userCanEditRoles) return [];

  // If one of the selected users has a role that the current user cannot edit, return no roles.
  // This can lead to problems if a role has been deactivated for a survey but some users still have it.
  // This is a user error and not a bug according to https://gitlab.evolution-online.net/jan.mierke/hrqs/-/issues/124
  const userWithSuperiorRole = selectedUsers.some(
    ({ role }) => role && !userCanEditRoles.some(({ id }) => id === role.id),
  );
  if (userWithSuperiorRole) return [];

  return userCanEditRoles.map((role) => ({
    label: role.displayName,
    value: String(role.id),
  }));
};

/**
 * All form fields available to the given user.
 */
const formFields = (user: User, selectedUsers: BaseUser[]): FormFieldData[] => [
  {
    name: 'moduleId',
    label: 'admin:Permission.Module',
    choices: findCurrentSurveyPermissions(user)
      .filter(
        (permission) => permission.permission_group === PermissionGroup.ADMIN,
      )
      .map((permission) => ({
        label: permission.module.name,
        value: `${permission.module.id}`,
      })),
  },
  binaryChoice('hasAccess', 'admin:Permission.AccessToTool'),
  binaryChoice('readOnly', 'admin:Permission.ReadOnlyAccessToTool'),
  {
    name: 'permissionGroup',
    label: 'admin:Permission.UserRole',
    choices: [
      {
        label: 'common:unchanged',
        value: '',
      },
      {
        label: PERMISSION_GROUP_NAME[PermissionGroup.ADMIN],
        value: `${PermissionGroup.ADMIN}`,
      },
      {
        label: PERMISSION_GROUP_NAME[PermissionGroup.USER],
        value: `${PermissionGroup.USER}`,
      },
    ],
  },
  binaryChoice(
    'includeAllSubnodes',
    'admin:BulkEdit.EditModulePermissionSubnodes',
  ),
  {
    name: 'isUserActiveInSurvey',
    label: 'admin:Detail.surveyActivationStatus',
    choices: [
      {
        label: 'common:unchanged',
        value: '',
      },
      { label: 'admin:Detail.active', value: 'true' },
      { label: 'admin:Detail.inactive', value: 'false' },
    ],
  },
  {
    name: 'role',
    label: 'admin:Detail.role',
    choices: [
      {
        label: 'common:unchanged',
        value: '',
      },

      ...getRoleSelection(user, selectedUsers),
    ],
  },
];

const emptyToNull = (value: any) => (value === '' ? null : value);

const atLeastOne = (keys: string[]) => ({
  name: 'at-least-one',
  test: (values: any, context: Yup.TestContext<any>) => {
    const isValid = _.some(
      _.pick(values, keys),
      (value) => value !== undefined,
    );

    if (isValid) return true;

    return context.createError({
      path: keys.join(' | '),
      message: 'At least one field must be set.',
    });
  },
});

const validationSchemaUserAttributes = Yup.object({
  editType: Yup.string().matches(/userAttributes/),
  isUserActiveInSurvey: Yup.string()
    .oneOf(['true', 'false'])
    .nullable()
    .transform(emptyToNull),
  role: Yup.string().nullable().transform(emptyToNull),
}).test(atLeastOne(['isUserActiveInSurvey', 'role']));

const validationSchemaModulePermission = (
  moduleIdTypeMap: ModuleIdTypeMapProps,
) =>
  Yup.object({
    editType: Yup.string().matches(/modulePermission/),
    moduleId: Yup.number().required(),
    permissionGroup: Yup.string().nullable().transform(emptyToNull),
    hasAccess: Yup.string()
      .oneOf(['true', 'false'])
      .nullable()
      .transform(emptyToNull),
    includeAllSubnodes: Yup.string()
      .oneOf(['true', 'false'])
      .nullable()
      .transform(emptyToNull),
    readOnly: Yup.string()
      .oneOf(['true', 'false'])
      .nullable()
      .transform(emptyToNull)
      .when('moduleId', {
        is: (moduleId: number) =>
          SECTIONS_WITH_READ_ONLY_OPTION.includes(moduleIdTypeMap[moduleId]),
        then: (schema) => schema, // keep it if moduleId is follow up
        otherwise: (schema) => schema.strip(), // remove if not
      }),
  }).test(
    atLeastOne([
      'permissionGroup',
      'hasAccess',
      'includeAllSubnodes',
      'readOnly',
    ]),
  );

export const validationSchemaByStep = (
  step: BulkEditStep,
  moduleIdTypeMap: ModuleIdTypeMapProps,
) =>
  ({
    [BulkEditStep.SelectType]: Yup.object({
      editType: Yup.string()
        .oneOf(['modulePermission', 'userAttributes'])
        .required(),
    }),
    [BulkEditStep.ChangeAttributes]: Yup.lazy((values) => {
      switch (values.editType) {
        case 'modulePermission':
          return validationSchemaModulePermission(moduleIdTypeMap);
        case 'userAttributes':
          return validationSchemaUserAttributes;
        default:
          return Yup.mixed().test('always-invalid', '', () => false);
      }
    }),
    [BulkEditStep.ConfirmChanges]: Yup.mixed(),
  })[step];

export interface BulkEditContextValue {
  bulkEditingStep: BulkEditStep;
  formFields: FormFieldData[];
  setBulkEditingStep: Dispatch<SetStateAction<BulkEditStep>>;
  userCount: number;
  selectedUsers: BaseUser[];
}

/**
 * Context for the entire bulk edit dialog.
 */
export const BulkEditContext = React.createContext<BulkEditContextValue>({
  bulkEditingStep: BulkEditStep.SelectType,
  formFields: [],
  setBulkEditingStep: () => {},
  userCount: 0,
  selectedUsers: [],
});

/**
 * Get FormFieldData for provided FormField name.
 */
export const useFormField = (name: string) => {
  const { formFields } = useContext(BulkEditContext);

  return useMemo(
    () => formFields.find((field) => field.name === name),
    [formFields, name],
  );
};

/**
 * Component that lets user select the edit type
 */
const BulkEditStepSelectEditType: FC = () => {
  const { t } = useTranslation('admin');
  const { values, setFieldValue } =
    useFormikContext<BulkEditDialogFormValues>();

  const { setBulkEditingStep } = useContext(BulkEditContext);

  const selectEditType = (type: typeof values.editType) => () => {
    setFieldValue('editType', type);
    setBulkEditingStep(BulkEditStep.ChangeAttributes);
  };

  return (
    <>
      <Stack sx={styles.questionStack}>
        <Typography variant="h4" sx={styles.editTypeQuestion}>
          {t('BulkEdit.EditTypeQuestion')}
        </Typography>
        <Typography variant="h6" sx={styles.questionSubtitle}>
          <Trans i18nKey="admin:BulkEdit.EditTypeQuestionSubtitle" />
        </Typography>
      </Stack>
      <Grid container spacing={2.75}>
        <Grid item xs={12} sm={6}>
          <ToggleButton
            data-test-id="user-admin-module-permission-edit-button"
            fullWidth
            onClick={selectEditType('modulePermission')}
            selected={values.editType === 'modulePermission'}
            sx={styles.toggleButton}
            value="modulePermission"
          >
            <Typography variant="body2">
              {t('BulkEdit.EditToolAccessButton')}
            </Typography>
          </ToggleButton>
        </Grid>
        <Grid item xs={12} sm={6}>
          <ToggleButton
            data-test-id="user-admin-user-bulk-edit-button"
            fullWidth
            onClick={selectEditType('userAttributes')}
            selected={values.editType === 'userAttributes'}
            sx={styles.toggleButton}
            value="userAttributes"
          >
            <Typography variant="body2">
              {t('BulkEdit.EditUserDataButton')}
            </Typography>
          </ToggleButton>
        </Grid>
      </Grid>
    </>
  );
};
interface ModuleIdTypeMapContentProps {
  moduleIdTypeMap: ModuleIdTypeMapProps;
}
/**
 * Component that lets user edit module permissions.
 */
const BulkEditStepModulePermission: FC<ModuleIdTypeMapContentProps> = ({
  moduleIdTypeMap,
}) => {
  const { values } = useFormikContext<BulkEditDialogFormValues>();
  const readOnlyField = SECTIONS_WITH_READ_ONLY_OPTION.includes(
    moduleIdTypeMap[Number(values.moduleId)],
  );
  return (
    <>
      <Typography variant="h4">
        <Trans i18nKey="admin:BulkEdit.EditModulePermissionHeader" />
      </Typography>
      <BulkEditRadioGroup name="moduleId" />
      <Typography variant="h4">
        <Trans i18nKey="admin:BulkEdit.EditModulePermissionOptions" />
      </Typography>
      <BulkEditRadioGroup name="hasAccess" />
      {readOnlyField && <BulkEditRadioGroup name="readOnly" />}
      <BulkEditRadioGroup name="permissionGroup" />
      <BulkEditRadioGroup name="includeAllSubnodes" />
    </>
  );
};
/**
 * Component that lets user edit user data.
 */
const BulkEditStepUserData: FC = () => {
  const { t } = useTranslation('admin');
  return (
    <>
      <Typography variant="h4">{t('BulkEdit.EditUserDataHeader')}</Typography>
      <BulkEditRadioGroup name="isUserActiveInSurvey" />
      <BulkEditRadioGroup name="role" />
    </>
  );
};
interface BulkEditStepConfirmProps {
  moduleRoles: ModuleRole[];
}
const BulkEditStepConfirm: FC<BulkEditStepConfirmProps> = ({ moduleRoles }) => {
  const { values } = useFormikContext<BulkEditDialogFormValues>();
  const { user } = useUserContext();
  const { moduleIdTypeMap } = useUserPermissions();
  const { userCount } = useContext(BulkEditContext);
  if (!user) return null;
  // Use the validation schema for the ChangeAttributes-step, since depending on the
  // edit type, different values will be present.
  const schema = validationSchemaByStep(
    BulkEditStep.ChangeAttributes,
    moduleIdTypeMap,
  );
  const validatedValues: Record<string, string> = schema.cast(values, {
    stripUnknown: true,
  });
  const omitValues = ['editType', 'moduleId'];
  const changedAttributes = Object.entries(validatedValues).reduce(
    (acc: Array<[string, string]>, [key, value]) =>
      omitValues.includes(key) || !value
        ? acc
        : [...acc, [key, value] as [string, string]],
    [],
  );
  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Typography variant="h4">
        <Trans i18nKey="admin:BulkEdit.ConfirmationHeader" count={userCount} />
      </Typography>
      {'moduleId' in validatedValues && (
        <Typography>
          <Trans
            i18nKey="admin:BulkEdit.ConfirmationModule"
            values={{
              module:
                findCurrentSurveyPermissions(user).find(
                  (permission) => `${permission.module.id}` === values.moduleId,
                )?.module.name ?? '',
            }}
          />
        </Typography>
      )}
      <BulkEditSummary changedAttributes={changedAttributes} />
      {/* If less than 10 users: list which users are losing permissions by which role change */}
      {changedAttributes.flat().includes('role') &&
        (userCount < 10 ? (
          <BulkEditPermissionSummary
            changedAttributes={changedAttributes}
            moduleRoles={moduleRoles}
          />
        ) : (
          <Typography variant="h4">
            <Trans
              i18nKey="admin:BulkEdit.ConfirmationHeaderPermissionsOverThreshold"
              count={userCount}
            />
          </Typography>
        ))}
      <BulkEditUserRoleSummary
        changedAttributes={changedAttributes}
        admin={user}
      />
    </Stack>
  );
};

/**
 * Main dialog content
 */
const BulkEditDialogContent: FC<ModuleIdTypeMapContentProps> = ({
  moduleIdTypeMap,
}) => {
  const { values } = useFormikContext<BulkEditDialogFormValues>();
  const { bulkEditingStep } = useContext(BulkEditContext);
  const [moduleRoles, setModuleRoles] = useState([]);

  useEffect(() => {
    HttpServiceInstance.get(`${apiPaths.MODULE_ROLE}`, {})
      .then(({ data }) => {
        setModuleRoles(data || []);
      })
      .catch((_error) => {
        console.warn('Could not get roles for user.');
      });
  }, []);

  switch (bulkEditingStep) {
    case BulkEditStep.SelectType:
      return <BulkEditStepSelectEditType />;

    case BulkEditStep.ConfirmChanges:
      return <BulkEditStepConfirm moduleRoles={moduleRoles} />;

    default:
      switch (values.editType) {
        case 'userAttributes':
          return <BulkEditStepUserData />;

        case 'modulePermission':
          return (
            <BulkEditStepModulePermission moduleIdTypeMap={moduleIdTypeMap} />
          );
      }
  }

  return null;
};

export type BulkEditDialogValidatedValues =
  | InferType<typeof validationSchemaUserAttributes>
  // InferType does not accept typeof validationSchemaModulePermission due to parameter
  | SchemaOf<{
      editType: string;
      moduleId: number;
      permissionGroup?: string;
      hasAccess: string;
      includeAllSubnodes: string;
      readOnly: string;
    }>;

/**
 * The main component
 */
interface BulkEditDialogProps extends Omit<DialogProps, 'onSubmit'> {
  onSubmit: (values: BulkEditDialogValidatedValues) => void;
  selectedUsers: BaseUser[];
  userCount: number;
}

export const BulkEditDialog: FC<BulkEditDialogProps> = ({
  open,
  onClose,
  onSubmit,
  sx = [],
  userCount,
  selectedUsers,
  ...props
}) => {
  const { t } = useTranslation('admin');
  const { user } = useUserContext();
  const { moduleIdTypeMap } = useUserPermissions();
  const { filterGroups } = useFilterContext();
  const formikRef = useRef<FormikProps<BulkEditDialogFormValues>>(null);
  const hasAppliedFilters = Object.keys(filterGroups).length > 0;
  const previouslyOpen = usePrevious(open);
  const dialogJustOpened = open && !previouslyOpen;
  const [bulkEditingStep, setBulkEditingStep] = useState(
    BulkEditStep.SelectType,
  );

  const schema = useMemo(
    () => validationSchemaByStep(bulkEditingStep, moduleIdTypeMap),
    [bulkEditingStep],
  );

  /**
   * Handle submit.
   */
  const handleSubmit = (values: FormikValues) => {
    const validatedValues: BulkEditDialogValidatedValues =
      validationSchemaByStep(
        BulkEditStep.ChangeAttributes,
        moduleIdTypeMap,
      ).cast(values, {
        stripUnknown: true,
      });

    formikRef.current?.resetForm();
    onSubmit(validatedValues);
  };

  useEffect(() => {
    if (dialogJustOpened) {
      setBulkEditingStep(BulkEditStep.SelectType);
      formikRef.current?.resetForm();
    }
  }, [dialogJustOpened, formikRef.current]);

  useEffect(() => {
    // Imperatively re-validate the form, whenever the schema changes.
    // Unfortunately formik doesn't do this out of the box, but instead only
    // re-validates, when the values change.
    // We cannot call this function in the useMemo above, since the new schema calculated there
    // will not yet have propagated to the validateForm() method.
    formikRef.current?.validateForm();
  }, [schema]);

  if (!user) return null;

  return (
    <BulkEditContext.Provider
      value={{
        bulkEditingStep,
        formFields: formFields(user, selectedUsers),
        setBulkEditingStep,
        userCount,
        selectedUsers,
      }}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialFormValues}
        initialErrors={{ editType: 'error' }}
        onSubmit={handleSubmit}
        validateOnMount={true}
        validationSchema={schema}
      >
        <Dialog
          className="BulkEditDialog-root"
          closeAfterTransition
          data-test-id="user-admin-bulk-edit-dialog"
          keepMounted={false}
          onClose={onClose}
          open={open}
          sx={{ ...styles.bulkEditDialog, ...(Array.isArray(sx) ? sx : [sx]) }}
          {...props}
        >
          <DialogContent>
            <Form>
              <Stack
                sx={{
                  gap: 2,
                }}
              >
                {hasAppliedFilters && (
                  <Grid
                    container
                    direction="row"
                    sx={{
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Grid item>
                      <Typography>{t('BulkEdit.FilterOverview')}</Typography>
                    </Grid>

                    <Grid item>
                      <FilterDisplay formatLabel={formatLabel(t)} readonly />
                    </Grid>
                  </Grid>
                )}

                <BulkEditStepper />

                <BulkEditDialogContent moduleIdTypeMap={moduleIdTypeMap} />
              </Stack>
            </Form>
          </DialogContent>

          <Divider sx={styles.divider} />
          <BulkEditDialogActions onClose={onClose} />
        </Dialog>
      </Formik>
    </BulkEditContext.Provider>
  );
};
